import * as React from 'react'
import { useSelector } from 'react-redux'

import './PageLogo.scss'

import defaultLogo from '../../logo.svg'

function extractDominantColor(blob) {
    return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => {
            const dataUrl = reader.result

            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            const size = 64
            canvas.width = size
            canvas.height = size

            const img = new Image()
            img.crossOrigin = 'anonymous'
            img.onload = () => {
                ctx.drawImage(img, 0, 0, size, size)
                let data
                try {
                    data = ctx.getImageData(0, 0, size, size).data
                } catch (e) {
                    resolve(null)
                    return
                }

                const colorCounts = {}
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3]
                    if (a < 128) continue
                    const brightness = (r + g + b) / 3
                    if (brightness > 240 || brightness < 15) continue
                    const qr = Math.round(r / 16) * 16
                    const qg = Math.round(g / 16) * 16
                    const qb = Math.round(b / 16) * 16
                    const key = `${qr},${qg},${qb}`
                    colorCounts[key] = (colorCounts[key] || 0) + 1
                }

                let best = null
                let bestScore = 0
                for (const [key, count] of Object.entries(colorCounts)) {
                    const [r, g, b] = key.split(',').map(Number)
                    const max = Math.max(r, g, b)
                    const min = Math.min(r, g, b)
                    const saturation = max === 0 ? 0 : (max - min) / max
                    const score = count * (0.3 + saturation * 0.7)
                    if (score > bestScore) {
                        bestScore = score
                        best = key
                    }
                }

                if (best) {
                    const [r, g, b] = best.split(',').map(Number)
                    resolve({ r, g, b })
                } else {
                    resolve(null)
                }
            }
            img.onerror = () => resolve(null)
            img.src = dataUrl
        }
        reader.onerror = () => resolve(null)
        reader.readAsDataURL(blob)
    })
}

function rgb(c) {
    return `rgb(${c.r}, ${c.g}, ${c.b})`
}

function adjustForHeader({ r, g, b }) {
    const brightness = (r * 299 + g * 587 + b * 114) / 1000
    let factor = 1
    if (brightness > 180) factor = 0.55
    else if (brightness > 140) factor = 0.7
    else if (brightness < 40) factor = 2.0
    else if (brightness < 80) factor = 1.3
    return {
        r: Math.min(255, Math.round(r * factor)),
        g: Math.min(255, Math.round(g * factor)),
        b: Math.min(255, Math.round(b * factor))
    }
}

function lightenColor({ r, g, b }, factor) {
    return {
        r: Math.round(r + (255 - r) * factor),
        g: Math.round(g + (255 - g) * factor),
        b: Math.round(b + (255 - b) * factor)
    }
}

function desaturate({ r, g, b }, factor) {
    const gray = Math.round(r * 0.299 + g * 0.587 + b * 0.114)
    return {
        r: Math.round(r + (gray - r) * factor),
        g: Math.round(g + (gray - g) * factor),
        b: Math.round(b + (gray - b) * factor)
    }
}

function applyThemeColors(color) {
    const root = document.getElementById('page-layout')
    if (!root || !color) return

    const primary = adjustForHeader(color)
    const contentBg = lightenColor(desaturate(primary, 0.3), 0.78)
    const toolbarBg = lightenColor(primary, 0.75)
    const dialogBg = lightenColor(desaturate(primary, 0.2), 0.25)
    const loginBg = lightenColor(desaturate(primary, 0.3), 0.6)

    root.style.setProperty('--theme-primary', rgb(primary))
    root.style.setProperty('--theme-content-bg', rgb(contentBg))
    root.style.setProperty('--theme-toolbar-bg', rgb(toolbarBg))
    root.style.setProperty('--theme-dialog-bg', rgb(dialogBg))
    root.style.setProperty('--theme-login-bg', rgb(loginBg))
}

function resetThemeColors() {
    const root = document.getElementById('page-layout')
    if (!root) return
    root.style.removeProperty('--theme-primary')
    root.style.removeProperty('--theme-content-bg')
    root.style.removeProperty('--theme-toolbar-bg')
    root.style.removeProperty('--theme-dialog-bg')
    root.style.removeProperty('--theme-login-bg')
}

export default function PageLogo() {
    const user = useSelector((state) => state.user)
    const [logoSrc, setLogoSrc] = React.useState(defaultLogo)

    React.useEffect(() => {
        let blobUrl = null

        if (user?.lab) {
            const labId = typeof user.lab === 'string' ? user.lab : user.lab.toString('hex')
            fetch(`/api/labs/logo/${labId}`)
                .then(response => {
                    if (response.ok) return response.blob()
                    throw new Error('No logo')
                })
                .then(blob => {
                    blobUrl = URL.createObjectURL(blob)
                    setLogoSrc(blobUrl)
                    extractDominantColor(blob).then(applyThemeColors)
                })
                .catch(() => {
                    setLogoSrc(defaultLogo)
                    resetThemeColors()
                })
        } else {
            setLogoSrc(defaultLogo)
            resetThemeColors()
        }

        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl)
        }
    }, [user?.lab])

    return(
        <div className="page-logo">
            <div className="logo-container">
                <img className="logo" src={logoSrc} alt="Logo" />
            </div>
        </div>
    )
}
